import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import dayjs from 'dayjs';
import { differenceInCalendarDays } from 'date-fns';
import {
    AddTask, QueryTaskParams,
    Task,
    TaskConfig,
    TaskConfigField, TaskFieldContent,
    TaskLabel, TaskResource,
    TaskSwimLane, TaskTester,
} from '@app/interfaces/lm/kanban-board';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SelectOption } from '@app/interfaces/sys/dict';
import { AccountService } from '@services/sys/account.service';
import { ResourcesService } from '@services/rm/resources.service';
import { Account, MessageTip } from '@app/interfaces/sys/account';
import { NzModalService } from 'ng-zorro-antd/modal';
import { InputContentComponent } from '@app/task-shared/components/task-detail/components/input-content/input-content.component';
import { TaskResourcesComponent } from '@app/task-shared/components/task-detail/components/task-resources/task-resources.component';
import { TaskTesterComponent } from '@app/task-shared/components/task-detail/components/task-tester/task-tester.component';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { LabelSelectComponent } from '../task-fields/components/label-select/label-select.component';
import { AssignMemberComponent } from '../task-fields/components/assign-member/assign-member.component';
import { DateSelectComponent } from '../task-fields/components/date-select/date-select.component';

@Component({
    selector: 'app-task-create-form',
    templateUrl: './task-create-form.component.html',
    styleUrls: ['./task-create-form.component.less']
})
export class TaskCreateFormComponent implements OnInit {
    @ViewChild('descInput') descInput!: InputContentComponent;
    @ViewChild('resourcesCom') resourcesCom!: TaskResourcesComponent;
    @ViewChild('testersCom') testersCom!: TaskTesterComponent;
    @ViewChildren(NzSelectComponent) selects!: QueryList<NzSelectComponent>;
    @ViewChildren(NzDatePickerComponent) datePickers!: QueryList<NzDatePickerComponent>;
    @ViewChildren(LabelSelectComponent) labelSelects!: QueryList<LabelSelectComponent>;
    @ViewChildren(AssignMemberComponent) assignMembers!: QueryList<AssignMemberComponent>;
    @ViewChildren(DateSelectComponent) dateSelects!: QueryList<DateSelectComponent>;
    @Input() editData: Task | undefined;
    @Input() projectId!: string;
    @Input() taskId!: string;
    validateForm!: FormGroup;

    configFields: TaskConfigField[] = [];   // 非固定字段
    projectMembers: SelectOption[] = [];
    priorityOptions: SelectOption[] = [];
    addLabelLoading = false;
    taskOptions: Task[] = [];
    parentTaskOptions: Task[] = [];
    taskConfigs: TaskConfig[] = [];
    taskSwimLanes: TaskSwimLane[] = [];
    formFields: Array<TaskConfigField[]> = [[], []];
    autoTips: Record<string, Record<string, string>> = {
        default: {
            required: 'This field is required',
            email: 'The input is not valid email',
            startDateError: 'The start date must earlier than the due date',
            endDateError: 'The due date cannot be earlier than the start date',
            isBeforeDate: 'The date cannot be earlier than the today',
            pattern: 'Please enter a valid URL address'
        },
    };
    quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
            ['blockquote'],//, 'code-block'

            // [{ 'header': 1 }, { 'header': 2 }],               // custom button values
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            // [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
            // [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent
            // [{ 'direction': 'rtl' }],                         // text direction

            [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
            // [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

            [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
            // [{ 'font': [] }],
            // [{ 'align': [] }],

            //['clean'],                                         // remove formatting button

            ['link', 'image', 'video']                      // link and image, video
        ]
    };
    accountOptions: SelectOption[] = [];
    accountList: Account[] = [];
    get typeId(): string {
        return this.validateForm.value.type;
    }
    allCategories: {
        [categoryId: string]: boolean
    } = {};
    lastConfigId!: string;
    preventChangeEvent = false;
    hasTimeCategories: string[] = ['Resource', 'Tester'];
    pageSize = 10;
    taskLoading = false;
    parentTaskLoading = false;
    pageIndex = 0;
    parentTaskIndex = 0;
    linkTaskTimer: number | null = null;
    parentTaskTimer: number | null = null;
    linkSearchTaskCount = 0;
    parentTaskSearchCount = 0;
    linkSearchKeyword = '';
    parentTaskSearchKeyword = '';
    focusedField: string = '';
    private scrollStartTop: { [key: string]: number | null } = {};
    private scrollThreshold = 50;

    onColumnScroll(event: Event): void {
        const target = event.target as Element;
        if (!target) return;
        const scrollId = target.className || 'default';
        const currentScrollTop = target.scrollTop ?? 0;

        // Record scroll position at start of scroll
        if (this.scrollStartTop[scrollId] === null || this.scrollStartTop[scrollId] === undefined) {
            this.scrollStartTop[scrollId] = currentScrollTop;
        }

        const scrollDelta = Math.abs(currentScrollTop - this.scrollStartTop[scrollId]!);

        if (scrollDelta >= this.scrollThreshold) {
            this.closeAllDropdowns();
            // Reset after closing so next scroll interaction starts fresh
            this.scrollStartTop[scrollId] = null;
        }
    }

    resetScrollTracking(): void {
        this.scrollStartTop = {};
    }

    private closeAllDropdowns(): void {
        // Close all open nz-select dropdowns
        this.selects?.forEach(select => {
            select.setOpenState(false);
        });
        // Close all open date pickers
        this.datePickers?.forEach(picker => {
            picker.close();
        });
        // Close child component dropdowns
        this.labelSelects?.forEach(comp => comp.closeDropdown());
        this.assignMembers?.forEach(comp => comp.closeDropdown());
        this.dateSelects?.forEach(comp => comp.closeDropdown());
        // Blur active element for any other inputs
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }

    constructor(private kanbanBoardService: KanbanBoardService,
        private message: NzMessageService,
        private accountService: AccountService,
        private modal: NzModalService,
        private resourcesService: ResourcesService,
        private fb: FormBuilder) { }

    startDateValidator = (control: FormControl): { [s: string]: boolean } => {
        const startTime = dayjs(control.value).format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        if (dayjs(startTime).isBefore(today)) {
            return {
                isBeforeDate: true,
                error: true,
            };
        }
        const dueTime = dayjs(this.validateForm?.value?.dueDate).format('YYYY-MM-DD');
        if (dayjs(dueTime).isBefore(startTime)) {
            return {
                startDateError: true,
                error: true,
            };
        }
        return {};
    }

    disabledDate = (current: Date): boolean => {
        return differenceInCalendarDays(current, new Date()) < 0;
    }

    async fetchAllAccount(): Promise<void> {
        const query = {
            page: 0,
            size: 999999,
            enable: true,
        };
        const { content } = await this.accountService.find(query);
        this.accountList = content;
    }

    endDateValidator = (control: FormControl): { [s: string]: boolean } => {
        const dueTime = dayjs(control.value).format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');

        if (dayjs(dueTime).isBefore(today)) {
            return {
                isBeforeDate: true,
                error: true,
            };
        }
        const startTime = dayjs(this.validateForm?.value?.startDate).format('YYYY-MM-DD');
        if (dayjs(dueTime).isBefore(startTime)) {
            return {
                endDateError: true,
                error: true,
            };
        }
        return {};
    }

    initFields(): void {
        const defaultFields = this.kanbanBoardService.getTaskDefaultFields();
        this.getConfigPanel(defaultFields as any);
    }

    async getCategoryInfos(): Promise<void> {
        const allCategories = await this.kanbanBoardService.getAllCategory();
        let data = {};
        allCategories.forEach(it => {
            data = {
                ...data,
                [it.id]: it.isPlural
            };
        });
        this.allCategories = data;
    }

    async ngOnInit(): Promise<void> {
        this.initFields();
        this.priorityOptions = this.kanbanBoardService.getTaskPriorityOptions();
        const commonFields = {
            taskName: [null, [Validators.required, Validators.maxLength(100)]],
            configId: [null, [Validators.required]],
            startDate: [null, [Validators.required, this.startDateValidator]],
            dueDate: [null, [Validators.required, this.endDateValidator]],
            swimLaneId: [null, [Validators.required]],
            description: [null, [Validators.maxLength(10000)]],
            assignees: [[], [Validators.required]],
            // testers: [[]],
            priority: [],
            labels: [[]],
            linkedTaskIds: [[]],
            parentTaskId: [],
        };
        this.validateForm = this.fb.group({
            ...commonFields
        });
        await this.fetchAllAccount();
        await Promise.all([
            this.getCategoryInfos(),
            this.getTaskConfigs(),
            this.getAllTaskSwimLane(),
            this.getAllProjectMembers(),
        ]);
        if (this.editData) {
            this.validateForm?.reset(this.editData);
        }
        if (this.taskId) {
            this.validateForm.reset({
                ...this.validateForm.value,
                parentTaskId: this.taskId
            });
        }
    }

    async getTaskConfigs(): Promise<void> {
        const arr = await this.kanbanBoardService.getAllTaskConfig();
        this.taskConfigs = arr;
    }

    async getAllTaskSwimLane(): Promise<void> {
        const arr = await this.kanbanBoardService.getSwimLanesByProjectId(this.projectId);
        this.taskSwimLanes = arr;
    }

    // async fetchPrimes(): Promise<void> {
    //     this.primeOptions = await this.taskService.findPrimeCandidates({});
    // }

    resetForm(): void {
        this.validateForm.reset();
        for (const control of Object.values(this.validateForm.controls)) {
            control.markAsPristine();
            control.updateValueAndValidity();
        }
    }

    validHasTimeField(): boolean {
        const hasResource = this.configFields.find(it => it.categoryName === 'Resource');
        if (hasResource && !this.resourcesCom.validForm()) {
            return false;
        }
        const hasTester = this.configFields.find(it => it.categoryName === 'Tester');
        if (hasTester && !this.testersCom.validForm()) {
            return false;
        }
        return true;
    }

    validForm(): AddTask | undefined {
        for (const control of Object.values(this.validateForm.controls)) {
            control.markAsDirty();
            control.updateValueAndValidity();
        }
        if (!this.validateForm.valid || !this.validHasTimeField()) {
            return;
        }
        const newFiles = this.descInput.addedFiles;
        const params = {
            ...this.validateForm.value
        };
        const formData = this.validateForm.value;
        // const { startDate, dueDate } = formData;
        // if (startDate && dueDate && dayjs(startDate).isSame(dayjs(dueDate), 'day')) {
        //     this.message.warning('Start date and due date cannot be the same day.');
        //     return;
        // }
        let resources: TaskResource[] = [];
        let testers: TaskTester[] = [];
        const taskFieldContents: TaskFieldContent[] = [];
        this.configFields.forEach(it => {
            const { id, defaultValueRule, categoryName, fieldKey } = it;
            const value = formData[id];
            const isDefaultField = this.kanbanBoardService.isDefaultField(defaultValueRule);
            const isDefaultFile = isDefaultField && categoryName === 'Attachment';
            delete params[id];
            if (categoryName === 'Resource') {
                resources = this.resourcesCom.getParams();
            } else if (categoryName === 'Tester') {
                testers = this.testersCom.getParams();
            } else {
                if (!id) {
                    return;
                }
                taskFieldContents.push({
                    taskConfigFieldId: id,
                    value: isDefaultFile ? newFiles : this.kanbanBoardService.formatTaskFieldValue(it, value)
                });
            }
        });
        return {
            ...params,
            projectId: this.projectId,
            taskFieldContents,
            resources,
            testers
        };
    }

    changeTaskType(): void {
        const { description, configId } = this.validateForm.value;
        if (this.preventChangeEvent) {
            // 如果 preventChangeEvent 为 true，则直接返回，并重置标志
            this.preventChangeEvent = false;
            return;
        }
        if (description) {
            this.modal.confirm({
                nzTitle: 'The description you’ve already entered will be lost after selecting a new type.',
                nzOkText: 'Yes',
                nzCancelText: 'No',
                nzOkType: 'primary',
                nzOkDanger: true,
                nzStyle: { top: '40vh' },
                nzOnOk: async () => {
                    this.confirmChangeType();
                    this.validateForm.get('description')?.setValue('');
                },
                nzOnCancel: () => {
                    this.preventChangeEvent = true; // 设置标志以阻止 changeTaskType 触发
                    this.validateForm.get('configId')?.setValue(this.lastConfigId);
                },
            });
        } else {
            this.confirmChangeType();
        }


    }

    /* 确认切换taskType */
    confirmChangeType(): void {
        this.removeControl(this.configFields);
        const { configId } = this.validateForm.value;
        this.lastConfigId = configId;
        if (!configId) {
            this.configFields = [];
            return;
        }
        const selectConfig = this.taskConfigs.find(it => it.id === configId);
        this.getConfigPanel(selectConfig?.configFields || []);
    }

    // 获取左右区分之后的字段
    getConfigPanel(configFields: TaskConfigField[]): void {
        const orderConfigFields = configFields.sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
        const leftPanel: TaskConfigField[] = [];
        const rightPanel: TaskConfigField[] = [];
        const fields: TaskConfigField[] = [];  // 非固定字段
        orderConfigFields.forEach(it => {
            let fieldData = it;
            const { id, isRequired, panel, categoryName, defaultValueRule } = it;
            const isDefaultField = this.kanbanBoardService.isDefaultField(defaultValueRule || '');
            if (!isDefaultField) {
                const newControl = new FormControl(null);
                const validators = [];
                if (isRequired && !this.hasTimeCategories.includes(categoryName as string)) {
                    validators.push(Validators.required);
                }
                if (categoryName === 'URL') {
                    validators.push((control: AbstractControl) => {
                        const value = control.value;
                        if (!value) return null;

                        const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9.-]+|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))(:[0-9]+)?(\/.*)?$/;

                        if (!urlPattern.test(value)) {
                            return { pattern: true };
                        }
                        let hostPart = value.replace(/^(https?:\/\/)?/, '').split(/[\/?#]/)[0];
                        const hostWithoutPort = hostPart.split(':')[0];

                        const isIP = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(hostWithoutPort);
                        const isLocalhost = hostWithoutPort === 'localhost';
                        const isDomain = hostWithoutPort.includes('.') && /[a-zA-Z]/.test(hostWithoutPort.split('.').pop() || '');

                        if (!isIP && !isLocalhost && !isDomain) {
                            return { pattern: true };
                        }

                        return null;
                    });
                }
                newControl.setValidators(validators);
                this.validateForm.addControl(
                    id,
                    newControl
                );
                fields.push({
                    ...it,
                    categoryIsPlural: this.allCategories?.[it.categoryId as string]
                });
                if (['Date', 'Timestamp'].includes(categoryName as string)) {
                    const setCurrentDate = defaultValueRule ? JSON.parse(defaultValueRule).setCurrentDate || false : false;
                    this.validateForm.get(id)?.setValue(setCurrentDate ? new Date() : '');
                }
            } else {
                const config = defaultValueRule ? JSON.parse(defaultValueRule) : {};
                fieldData = {
                    ...fieldData,
                    fieldKey: config?.fieldKey
                };
                if (categoryName === 'Attachment') {
                    fields.push({
                        ...it,
                        categoryIsPlural: this.allCategories?.[it.categoryId as string]
                    });
                }
            }
            panel === 'left' ? leftPanel.push(fieldData) : rightPanel.push(fieldData);
        });
        this.formFields = [
            leftPanel,
            rightPanel
        ];
        this.configFields = fields;
        console.log('formFields', this.formFields, configFields);
    }

    removeControl(arr: TaskConfigField[]): void {
        if (arr && arr.length > 0) {
            arr.forEach(it => {
                const { id } = it;
                if (!this.validateForm.get(id)) {
                    return;
                }
                this.validateForm.removeControl(id);
            });
        }
    }

    async loadTask(): Promise<void> {
        if (this.taskOptions.length >= this.linkSearchTaskCount || this.taskLoading) {
            return;
        }
        this.pageIndex++;
        await this.getLinkTask(false);
    }

    async getLinkTask(init: boolean = false): Promise<void> {
        try {
            if (init) {
                this.taskOptions = [];
                this.pageIndex = 0;
            }
            this.taskLoading = true;
            const params = {
                startRow: init ? 0 : this.pageIndex * this.pageSize + 1,
                endRow: init ? this.pageSize : (this.pageIndex + 1) * this.pageSize,
                keyword: this.linkSearchKeyword
            };
            const arr = await this.getProjectTasks(params);
            if (init) {
                const { count } = this.kanbanBoardService.extractUniqueCounts(arr);
                this.taskOptions = [...arr];
                this.linkSearchTaskCount = count;
            } else {
                this.taskOptions.push(...arr);
            }
        } finally {
            this.taskLoading = false;
        }
    }

    clearLinkTimer(): void {
        if (this.linkTaskTimer) {
            clearTimeout(this.linkTaskTimer);
            this.linkTaskTimer = null;
        }
    }

    clearParentTaskTimer(): void {
        if (this.parentTaskTimer) {
            clearTimeout(this.parentTaskTimer);
            this.parentTaskTimer = null;
        }
    }

    searchLinkTask(value: string): void {
        this.pageIndex = 0;
        this.clearLinkTimer();
        this.linkSearchKeyword = value;
        this.linkTaskTimer = window.setTimeout(async () => {
            await this.getLinkTask(true);
        }, 350);
    }


    searchParentTask(value: string): void {
        this.parentTaskIndex = 0;
        this.clearParentTaskTimer();
        this.parentTaskSearchKeyword = value;
        this.parentTaskTimer = window.setTimeout(async () => {
            await this.getParentTask(true);
        }, 350);
    }

    async loadParentTask(): Promise<void> {
        if (this.parentTaskOptions.length >= this.parentTaskSearchCount || this.parentTaskLoading) {
            return;
        }
        this.parentTaskIndex++;
        await this.getParentTask(false);
    }

    async getParentTask(init: boolean = false): Promise<void> {
        try {
            if (init) {
                this.parentTaskOptions = [];
            }
            this.parentTaskLoading = true;
            const params = {
                startRow: init ? 0 : this.parentTaskIndex * this.pageSize + 1,
                endRow: init ? this.pageSize : (this.parentTaskIndex + 1) * this.pageSize,
                keyword: this.parentTaskSearchKeyword
            };
            const arr = await this.getProjectTasks(params);
            if (init) {
                const { count } = this.kanbanBoardService.extractUniqueCounts(arr);
                this.parentTaskOptions = [...arr];
                this.parentTaskSearchCount = count;
            } else {
                this.parentTaskOptions.push(...arr);
            }
        } finally {
            this.parentTaskLoading = false;
        }
    }

    async getProjectTasks(otherParams: QueryTaskParams): Promise<Task[]> {
        const params = {
            projectId: this.projectId,
            isDESC: true,
            ...otherParams
        };
        const res = await this.kanbanBoardService.loadAllTasks(params);
        return res;
    }

    async getAllProjectMembers(): Promise<void> {
        // get all project members
        if (!this.projectId) {
            return;
        }
        // 暂时没有获取member的接口，通过project的详情接口找到
        const data = await this.kanbanBoardService.getProjectOne(this.projectId);
        const { projectMembers } = data;
        this.projectMembers = this.kanbanBoardService.getMembers(projectMembers, this.accountList);
        console.log('projectMembers', this.projectMembers, this.accountList);
    }
}
